import request from '../utils/request';
import type { ListResponse } from '../types/common';
import type { 
  InboundForecast, 
  CreateInboundForecastRequest,
  InboundRecord
} from '../types/inbound-forecast';

// 获取入库预报列表
export const getInboundForecastList = async (params: any) => {
  const response = await request.get<ListResponse<InboundForecast>>('/api/v1/inbound/inbound-forecasts/', {
    params,
  });
  return response.data;
};

// 获取入库预报详情
export const getInboundForecastDetail = async (id: number) => {
  const response = await request.get(`/api/v1/inbound/inbound-forecasts/${id}/`);
  return response.data;
};

// 搜索入库预报（支持物流单号、预报编号、项目编码等）
export const searchInboundForecasts = async (searchParams: {
  search?: string;           // 通用搜索词
  project_code?: string;     // 项目编码
  tracking_number?: string;  // 物流单号
  system_code?: string;      // 预报编号
  page?: number;
  page_size?: number;
}) => {
  const params: any = {
    page: searchParams.page || 1,
    page_size: searchParams.page_size || 20,
  };

  // 如果有通用搜索词，添加到搜索参数
  if (searchParams.search) {
    params.search = searchParams.search;
  }

  // 如果有项目编码，添加到过滤参数
  if (searchParams.project_code) {
    params.project__project_code = searchParams.project_code;
  }

  // 如果有物流单号，添加到过滤参数
  if (searchParams.tracking_number) {
    params.tracking_records__tracking_number = searchParams.tracking_number;
  }

  // 如果有预报编号，添加到过滤参数
  if (searchParams.system_code) {
    params.system_code = searchParams.system_code;
  }

  const response = await request.get<ListResponse<InboundForecast>>('/api/v1/inbound/inbound-forecasts/', {
    params,
  });
  return response.data;
};

// 创建入库预报
export const createInboundForecast = async (data: CreateInboundForecastRequest) => {
  // 验证必填字段
  if (!data.warehouse || typeof data.warehouse !== 'number') {
    console.error('验证失败：入库仓库ID无效');
    throw new Error('入库仓库ID无效');
  }
  if (!data.project || typeof data.project !== 'number') {
    console.error('验证失败：关联项目ID无效');
    throw new Error('关联项目ID无效');
  }
  // 预报名称现在是可选的，移除必填验证
  if (data.name !== undefined && data.name !== null && typeof data.name !== 'string') {
    console.error('验证失败：预报名称格式无效');
    throw new Error('预报名称格式无效');
  }
  
  // 确保数字类型字段为数字
  const submitData = {
    ...data,
    warehouse: Number(data.warehouse),
    project: Number(data.project),
    tracking_records_data: (data.tracking_records_data || []).map(group => ({
      logistics_provider: Number(group.logistics_provider),
      tracking_numbers: group.tracking_numbers
    })),
    containers_data: data.containers_data.map(container => ({
      ...container,
      parent_container: container.parent_container ? Number(container.parent_container) : undefined,
      // 尺寸/重量选填，未填写时保持undefined（不转成NaN），提交时JSON会自动省略该字段
      length: container.length !== undefined && container.length !== null ? Number(container.length) : undefined,
      width: container.width !== undefined && container.width !== null ? Number(container.width) : undefined,
      height: container.height !== undefined && container.height !== null ? Number(container.height) : undefined,
      weight: container.weight !== undefined && container.weight !== null ? Number(container.weight) : undefined,
      // 直接传递三态标注（可能为undefined/null/boolean）
      is_quick_inbound_declared: (container as any).is_quick_inbound_declared ?? null
    })),
    container_contents_data: data.container_contents_data.map(content => ({
      ...content,
      container: content.container,  // 保持临时ID不变
      inventory_unit: Number(content.inventory_unit),
      quantity: Number(content.quantity)
    }))
  };

  // API请求数据

  const response = await request.post<InboundForecast>('/api/v1/inbound/inbound-forecasts/', submitData);
  // 创建入库预报API - 成功
  return response.data;
};

// 创建前预检查（物流跟踪号重复提示）
export const precheckInboundForecast = async (data: CreateInboundForecastRequest) => {
  const submitData = {
    ...data,
    warehouse: Number(data.warehouse),
    project: Number(data.project),
    tracking_records_data: (data.tracking_records_data || []).map(group => ({
      logistics_provider: Number(group.logistics_provider),
      tracking_numbers: group.tracking_numbers
    })),
    containers_data: data.containers_data.map(container => ({
      ...container,
      parent_container: container.parent_container ? Number(container.parent_container) : undefined,
      // 尺寸/重量选填，未填写时保持undefined（不转成NaN），提交时JSON会自动省略该字段
      length: container.length !== undefined && container.length !== null ? Number(container.length) : undefined,
      width: container.width !== undefined && container.width !== null ? Number(container.width) : undefined,
      height: container.height !== undefined && container.height !== null ? Number(container.height) : undefined,
      weight: container.weight !== undefined && container.weight !== null ? Number(container.weight) : undefined,
      is_quick_inbound_declared: (container as any).is_quick_inbound_declared ?? null
    })),
    container_contents_data: data.container_contents_data.map(content => ({
      ...content,
      container: content.container,
      inventory_unit: Number(content.inventory_unit),
      quantity: Number(content.quantity)
    }))
  };
  const response = await request.post('/api/v1/inbound/inbound-forecasts/precheck/', submitData);
  return response.data;
};

// 删除入库预报
export const deleteInboundForecast = async (id: number) => {
  await request.delete(`/api/v1/inbound/inbound-forecasts/${id}/`);
};

// 取消入库预报：状态流转（已提交->已取消），不做物理删除
export const cancelInboundForecast = async (id: number) => {
  const response = await request.post(`/api/v1/inbound/inbound-forecasts/${id}/cancel/`);
  return response.data;
};

// 获取仓库列表
export const getWarehouseList = async () => {
  const response = await request.get<ListResponse<{
    id: number;
    name: string;
    code: string;
  }>>('/api/v1/warehouses/');
  return response.data;
};

// 获取项目列表
export const getProjectList = async () => {
  const response = await request.get<ListResponse<{
    id: number;
    name: string;
    project_code: string;
  }>>('/api/v1/project/projects/');
  return response.data;
};

// 获取物流服务商列表
export const getLogisticsProviderList = async () => {
  const response = await request.get<ListResponse<{
    id: number;
    name: string;
    code: string;
  }>>('/api/v1/logistics/providers/');
  return response.data;
};

// 生成单个库存单元标签
export async function generateUnitLabel(data: {
  name: string;
  system_code: string;
  forecast_code: string;
  quantity: number;
  unit_type: string;
  content_items?: Array<{
    product_name: string;
    quantity: number;
  }>;
}) {
  const response = await request.post('/api/v1/inbound/labels/unit/', data, {
    responseType: 'blob',
    headers: {
      'Accept': 'application/pdf,*/*',
      'Content-Type': 'application/json'
    }
  });
  return response;
}

// 批量生成库存单元标签
export async function generateUnitLabelsBatch(data: Array<{
  name: string;
  system_code: string;
  forecast_code: string;
  quantity: number;
  unit_type: string;
  content_items?: Array<{
    product_name: string;
    quantity: number;
  }>;
}>) {
  const response = await request.post('/api/v1/inbound/labels/unit/batch/', data, {
    responseType: 'blob',
    headers: {
      'Accept': 'application/pdf,*/*',
      'Content-Type': 'application/json'
    }
  });
  return response;
}

// 获取预报容器详细信息
export const getContainerDetails = async (containerCode: string) => {
  const response = await request.get(`/api/v1/inbound/containers/${containerCode}/details/`);
  return response.data;
}; 

// 根据物流单号查询预报信息（可选按当前项目/仓库过滤，降低跨项目/仓库误匹配的风险）；
// page_size 取20而不是1，因为同一物流单号可能匹配到多条预报，调用方需要感知并处理这种情况
export const getForecastByTrackingNumber = async (
  trackingNumber: string,
  filters?: { project_code?: string; warehouse_code?: string }
) => {
  const response = await request.get<ListResponse<InboundForecast>>('/api/v1/inbound/inbound-forecasts/', {
    params: {
      tracking_records__tracking_number: trackingNumber,
      ...(filters?.project_code ? { project_code: filters.project_code } : {}),
      ...(filters?.warehouse_code ? { warehouse_code: filters.warehouse_code } : {}),
      page: 1,
      page_size: 20
    }
  });
  return response.data;
};



// 创建入库记录
export const createInboundRecord = async (data: {
  project: string;
  warehouse: string;  // 新增：仓库编码字段
  inbound_forecast?: number;
  inbound_forecast_container?: string;  // 改为字符串类型，保存容器编码
  tracking_number?: string;
  is_quick_inbound: boolean;
  irregular_operation?: string[];  // 修改：非常规操作数组
  remarks: string;
  inventory_items: Array<{
    inventory_unit_code: string;
    quantity: number;
    remarks?: string;
  }>;
}) => {
  return request.post('/api/v1/inbound/records/', data);
};

// 获取入库记录列表
export const getInboundRecordList = async (params: {
  page?: number;
  page_size?: number;
  search?: string;
  project?: number;
  is_quick_inbound?: boolean;
  irregular_operation?: string;
  ordering?: string;
  inbound_forecast?: number;
  is_voided?: boolean;
}) => {
  const response = await request.get<ListResponse<InboundRecord>>('/api/v1/inbound/inbound-records/', {
    params,
  });
  return response.data;
};

// 检查物流单号是否已存在入库记录
export const checkTrackingNumberInbound = async (params: {
  tracking_number: string;
}) => {
  const response = await request.get('/api/v1/inbound/records/check-tracking-number/', {
    params
  });
  return response.data;
}; 

// 快捷入库API
export const quickInbound = async (
  containerScanCode: string,
  options?: { actual_quantity?: number; remarks?: string }
) => {
  const response = await request.post('/api/v1/inbound/quick-inbound/', {
    container_scan_code: containerScanCode,
    ...(options?.actual_quantity !== undefined ? { actual_quantity: options.actual_quantity } : {}),
    ...(options?.remarks ? { remarks: options.remarks } : {})
  });
  return response.data;
};

// ===== 批量创建入库预报（目前只支持"批量创建常规预报"这一个场景） =====

export interface InboundForecastImportJobGroupPreview {
  group_key: string;
  row_nos: number[];
  valid: boolean;
  error_message?: string;
  // 常规预报场景专用
  inventory_unit_count?: number;
  total_quantity?: number;
  // 非常规预报场景专用
  special_instructions_display?: string;
  container_type_display?: string;
  container_quantity?: number;
  // 两个场景共用（物流信息在两边都是选填）
  logistics_provider_code?: string;
  logistics_provider_name?: string;
  tracking_number_count?: number;
}

export interface InboundForecastImportJobSummary {
  total_rows?: number;
  total_groups?: number;
  valid_groups?: number;
  invalid_groups?: number;
  groups?: InboundForecastImportJobGroupPreview[];
  created?: number;
  failed?: number;
}

export interface InboundForecastImportJob {
  id: number;
  scenario: string;
  status: 'draft' | 'queued' | 'running' | 'done' | 'failed';
  file_name: string;
  source_file?: string;
  summary: InboundForecastImportJobSummary;
  error_message: string;
  created_at: string;
  updated_at: string;
}

export interface InboundForecastImportJobItem {
  id: number;
  job: number;
  group_key: string;
  status: 'pending' | 'created' | 'failed';
  row_nos: number[];
  raw_rows: any[];
  created_forecast: number | null;
  created_forecast_system_code: string | null;
  error_message: string;
  created_at: string;
  updated_at: string;
}

// 上传Excel/CSV，后端同步做一次校验并返回预检查摘要（此时还没创建任何预报）。
// scenario 区分"批量创建常规预报"/"批量创建非常规预报"，两个场景的模板列、校验逻辑完全不同
export const uploadInboundForecastImportFile = async (file: File, scenario: 'regular' | 'irregular' = 'regular') => {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('scenario', scenario);
  const response = await request.post<InboundForecastImportJob>(
    '/api/v1/inbound/inbound-forecast-import-jobs/',
    formData,
    { headers: { 'Content-Type': 'multipart/form-data' } }
  );
  return response.data;
};

// 确认后触发真正的异步创建
export const executeInboundForecastImportJob = async (jobId: number) => {
  const response = await request.post<InboundForecastImportJob>(
    `/api/v1/inbound/inbound-forecast-import-jobs/${jobId}/execute/`
  );
  return response.data;
};

// 轮询任务状态
export const getInboundForecastImportJob = async (jobId: number) => {
  const response = await request.get<InboundForecastImportJob>(
    `/api/v1/inbound/inbound-forecast-import-jobs/${jobId}/`
  );
  return response.data;
};

// 查看执行结果明细（已创建/失败）
export const getInboundForecastImportJobItems = async (jobId: number, status?: 'created' | 'failed') => {
  const response = await request.get<InboundForecastImportJobItem[]>(
    `/api/v1/inbound/inbound-forecast-import-jobs/${jobId}/items/`,
    { params: status ? { status } : {} }
  );
  return response.data;
};

// 模板文件改为前端用 exceljs 生成（和"批量创建一件代发指令"同款样式），不再走后端接口